
import React from "react";
import { CheckCircle, ChevronRight, Lightbulb, Shield, TrendingUp } from "lucide-react";
import { Link } from "react-router-dom";

const Features: React.FC = () => {
  return (
    <section className="section bg-gray-50">
      <div className="container-custom">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-techne-primaryPurple mb-4">
            Why AI Governance Matters for Your Board
          </h2>
          <p className="text-lg text-gray-600">
            AI technologies present unprecedented opportunities and risks for organizations.
            Effective board oversight is critical for responsible AI adoption.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Feature 1 */}
          <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100 hover:shadow-lg transition-shadow duration-300">
            <div className="w-12 h-12 bg-techne-primaryPurple/10 rounded-lg flex items-center justify-center mb-4">
              <Shield className="text-techne-primaryPurple w-6 h-6" />
            </div>
            <h3 className="text-xl font-semibold text-techne-primaryPurple mb-3">Risk Mitigation</h3>
            <p className="text-gray-600 mb-4">
              Identify and address AI-related risks before they impact your organization's reputation or operations.
            </p>
            <ul className="space-y-2 mb-6">
              <li className="flex items-start gap-2">
                <CheckCircle className="text-techne-purple w-5 h-5 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Compliance with emerging regulations</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="text-techne-purple w-5 h-5 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Ethical use of AI technologies</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="text-techne-purple w-5 h-5 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Data privacy and security protections</span>
              </li>
            </ul>
          </div>

          {/* Feature 2 */}
          <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100 hover:shadow-lg transition-shadow duration-300">
            <div className="w-12 h-12 bg-techne-purple/10 rounded-lg flex items-center justify-center mb-4">
              <TrendingUp className="text-techne-purple w-6 h-6" />
            </div>
            <h3 className="text-xl font-semibold text-techne-primaryPurple mb-3">Strategic Advantage</h3>
            <p className="text-gray-600 mb-4">
              Turn AI governance into a competitive advantage through responsible innovation and stakeholder trust.
            </p>
            <ul className="space-y-2 mb-6">
              <li className="flex items-start gap-2">
                <CheckCircle className="text-techne-purple w-5 h-5 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Board-level AI literacy and oversight</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="text-techne-purple w-5 h-5 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Clear governance frameworks</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="text-techne-purple w-5 h-5 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Transparent decision-making processes</span>
              </li>
            </ul>
          </div>

          {/* Feature 3 */}
          <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100 hover:shadow-lg transition-shadow duration-300">
            <div className="w-12 h-12 bg-techne-primaryPurple/10 rounded-lg flex items-center justify-center mb-4">
              <Lightbulb className="text-techne-primaryPurple w-6 h-6" />
            </div>
            <h3 className="text-xl font-semibold text-techne-primaryPurple mb-3">Future Readiness</h3>
            <p className="text-gray-600 mb-4">
              Prepare your organization for evolving AI technologies, regulations, and stakeholder expectations.
            </p>
            <ul className="space-y-2 mb-6">
              <li className="flex items-start gap-2">
                <CheckCircle className="text-techne-purple w-5 h-5 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Regular governance reviews</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="text-techne-purple w-5 h-5 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Continuous executive education</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="text-techne-purple w-5 h-5 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Adaptable risk management systems</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-12 text-center">
          <Link 
            to="/blueprint"
            className="inline-flex items-center text-techne-primaryPurple hover:text-techne-purple transition-colors"
          >
            Learn more about AI governance training
            <ChevronRight className="w-5 h-5 ml-1" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default Features;
